import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { CircularProgress } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import baseUrl from "../../Environment";

const quillModules = {
  toolbar: [
    [{ header: [false, 1, 2, 3] }],
    ["bold", "italic"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header", "bold", "italic", "color", "background",
  "align", "list", "bullet", "indent", "link",
];

const emptySection = () => ({
  _id: null,
  heading: "",
  content: "",
  imageFile: null,
  imagePreview: null,
  existingImage: null, // image URL already on the server
  deletedImage: false,
});

export default function BlogUploadPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [author, setAuthor] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  // SEO / share fields
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");

  const [heroImageFile, setHeroImageFile] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState(null);
  const [existingHeroImage, setExistingHeroImage] = useState(null);
  const [deletedHeroImage, setDeletedHeroImage] = useState(false);

  const [sections, setSections] = useState([emptySection()]);

  const [submitting, setSubmitting] = useState(false);
  const [loadingBlog, setLoadingBlog] = useState(isEditMode);

  // ---------- Load existing blog when editing ----------
  useEffect(() => {
    if (!isEditMode) return;

    const loadBlog = async () => {
      setLoadingBlog(true);
      try {
        const { data: blog } = await axios.get(`${baseUrl}blog/${id}`);
        setTitle(blog.title || "");
        setCategory(blog.category || "");
        setAuthor(blog.author || "");
        setDate(blog.date ? new Date(blog.date).toISOString().slice(0, 10) : "");
        setMetaTitle(blog.metaTitle || "");
        setMetaDescription(blog.metaDescription || "");
        setExistingHeroImage(blog.heroImage || null);

        const loadedSections = (blog.sections || [])
          .sort((a, b) => a.order - b.order)
          .map((s) => ({
            _id: s._id,
            heading: s.heading || "",
            content: s.content || "",
            imageFile: null,
            imagePreview: null,
            existingImage: s.image || null,
            deletedImage: false,
          }));
        setSections(loadedSections.length ? loadedSections : [emptySection()]);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load blog for editing.");
      } finally {
        setLoadingBlog(false);
      }
    };

    loadBlog();
  }, [id, isEditMode]);

  // ---------- Hero image ----------
  const handleHeroImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHeroImageFile(file);
    setHeroImagePreview(URL.createObjectURL(file));
    setDeletedHeroImage(false);
  };

  const removeHeroImage = () => {
    setHeroImageFile(null);
    setHeroImagePreview(null);
    if (existingHeroImage) setDeletedHeroImage(true);
    setExistingHeroImage(null);
  };

  // ---------- Sections ----------
  const updateSection = (index, field, value) => {
    setSections((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const handleSectionImageChange = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSections((prev) =>
      prev.map((s, i) =>
        i === index
          ? { ...s, imageFile: file, imagePreview: URL.createObjectURL(file), deletedImage: false }
          : s
      )
    );
  };

  const removeSectionImage = (index) => {
    setSections((prev) =>
      prev.map((s, i) =>
        i === index
          ? {
              ...s,
              imageFile: null,
              imagePreview: null,
              existingImage: null,
              deletedImage: s.existingImage ? true : s.deletedImage,
            }
          : s
      )
    );
  };

  const addSection = () => setSections((prev) => [...prev, emptySection()]);

  const removeSection = (index) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Title is required.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        title,
        category: category || null,
        author: author || null,
        date,
        metaTitle: metaTitle || null,
        metaDescription: metaDescription || null,
        deletedHeroImage,
        sections: sections.map((s) => ({
          _id: s._id,
          heading: s.heading,
          content: s.content,
          deletedImage: s.deletedImage,
        })),
      };

      const formData = new FormData();
      formData.append("data", JSON.stringify(payload));

      if (heroImageFile) {
        formData.append("heroImage", heroImageFile);
      }

      sections.forEach((s, i) => {
        if (s.imageFile) {
          formData.append(`sectionImage_${i}`, s.imageFile);
        }
      });

      if (isEditMode) {
        await axios.put(`${baseUrl}blog/${id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Blog updated successfully!");
      } else {
        await axios.post(`${baseUrl}blog`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Blog published successfully!");
      }

      // Give the toast a beat to render before leaving the page, then
      // head back to the blog list either way (create or edit).
      setTimeout(() => navigate("/blog"), 1000);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message || "Something went wrong while publishing."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingBlog) {
    return (
      <section className="bg-[#EEF4EA] min-h-screen py-16 flex justify-center items-start">
        <CircularProgress sx={{ color: "#3A683F", marginTop: "10rem" }} />
      </section>
    );
  }

  const heroPreviewToShow = heroImagePreview || existingHeroImage;

  return (
    <section className="bg-[#EEF4EA] min-h-screen py-16">
      <ToastContainer position="top-right" autoClose={2500} />
      <div className="sm:max-w-full md:max-w-[80%] xl:max-w-[60rem] w-full mx-auto">
        <div className="bg-white px-8 md:px-14 py-10 shadow-sm rounded">
          <h1 className="text-[#3A683F] font-lexend font-extralight text-[28px] md:text-[38px] uppercase mb-8">
            {isEditMode ? "Edit Blog Post" : "New Blog Post"}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-10">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-[#3A683F] font-lexend text-[15px] mb-1">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="How to Choose the Right Neck Pillow for Sleeping Comfort"
                  className="w-full border border-gray-300 rounded px-3 py-2 font-lexend text-[15px] focus:outline-none focus:border-[#3A683F]"
                />
              </div>

              <div>
                <label className="block text-[#3A683F] font-lexend text-[15px] mb-1">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 font-lexend text-[15px] focus:outline-none focus:border-[#3A683F]"
                />
              </div>

              <div>
                <label className="block text-[#3A683F] font-lexend text-[15px] mb-1">Category</label>
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Sleep Health"
                  className="w-full border border-gray-300 rounded px-3 py-2 font-lexend text-[15px] focus:outline-none focus:border-[#3A683F]"
                />
              </div>

              <div>
                <label className="block text-[#3A683F] font-lexend text-[15px] mb-1">Author</label>
                <input
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Optional"
                  className="w-full border border-gray-300 rounded px-3 py-2 font-lexend text-[15px] focus:outline-none focus:border-[#3A683F]"
                />
              </div>
            </div>

            {/* SEO / Meta fields */}
            <div>
              <h2 className="text-[#3A683F] font-lexend font-extralight text-[18px] mb-4">
                SEO / Share Details
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[#3A683F] font-lexend text-[15px] mb-1">
                    Meta Title
                  </label>
                  <input
                    type="text"
                    value={metaTitle}
                    onChange={(e) => setMetaTitle(e.target.value)}
                    placeholder="Shown in search engine results and browser tabs"
                    className="w-full border border-gray-300 rounded px-3 py-2 font-lexend text-[15px] focus:outline-none focus:border-[#3A683F]"
                  />
                </div>

                <div>
                  <label className="block text-[#3A683F] font-lexend text-[15px] mb-1">
                    Meta Description
                  </label>
                  <textarea
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    placeholder="Short summary shown in search results and social shares"
                    rows={3}
                    className="w-full border border-gray-300 rounded px-3 py-2 font-lexend text-[15px] focus:outline-none focus:border-[#3A683F] resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Hero image */}
            <div>
              <label className="block text-[#3A683F] font-lexend text-[15px] mb-2">
                Hero / Banner Image
              </label>
              <label className="flex items-center gap-2 w-fit border border-dashed border-[#3A683F] text-[#3A683F] px-4 py-2 rounded cursor-pointer font-lexend text-[14px] hover:bg-[#EEF4EA] transition">
                <CloudUploadIcon sx={{ fontSize: 18 }} />
                Upload image
                <input type="file" accept="image/*" onChange={handleHeroImageChange} className="hidden" />
              </label>
              {heroPreviewToShow && (
                <div className="relative w-full mt-4">
                  <img
                    src={heroPreviewToShow}
                    alt="Hero preview"
                    className="w-full h-[240px] object-cover rounded"
                  />
                  <button
                    type="button"
                    onClick={removeHeroImage}
                    aria-label="Remove hero image"
                    className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-gray-600 hover:text-red-500 shadow"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              )}
            </div>

            {/* Sections */}
            <div>
              <h2 className="text-[#3A683F] font-lexend font-extralight text-[118px] mb-4">
                Content Sections
              </h2>

              <div className="space-y-6">
                {sections.map((section, index) => {
                  const sectionPreview = section.imagePreview || section.existingImage;
                  return (
                    <div key={section._id || index} className="border border-gray-200 rounded p-6 relative space-y-5">
                      {sections.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSection(index)}
                          aria-label="Remove section"
                          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </button>
                      )}

                      <div>
                        <label className="block text-[#3A683F] font-lexend text-[14px] mb-2">Heading</label>
                        <input
                          type="text"
                          value={section.heading}
                          onChange={(e) => updateSection(index, "heading", e.target.value)}
                          placeholder="Can Pillows Cause Neck Pain?"
                          className="w-full border border-gray-300 rounded px-3 py-2 font-lexend text-[15px] focus:outline-none focus:border-[#3A683F]"
                        />
                      </div>

                      <div>
                        <label className="block text-[#3A683F] font-lexend text-[14px] mb-2">Content</label>
                        <div className="bg-white">
                          <ReactQuill
                            theme="snow"
                            value={section.content}
                            onChange={(html) => updateSection(index, "content", html)}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Write this section's content — bold key phrases, add bullet points, etc."
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[#3A683F] font-lexend text-[14px] mb-2">
                          Section image (optional)
                        </label>
                        <label className="inline-flex items-center gap-1.5 border border-dashed border-[#3A683F] text-[#3A683F] px-4 py-2 rounded cursor-pointer font-lexend text-[14px] hover:bg-[#EEF4EA] transition">
                          <CloudUploadIcon sx={{ fontSize: 18 }} />
                          Upload image
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleSectionImageChange(index, e)}
                            className="hidden"
                          />
                        </label>
                        {sectionPreview && (
                          <div className="relative w-full mt-4">
                            <img
                              src={sectionPreview}
                              alt={`Section ${index + 1} preview`}
                              className="w-full h-[200px] object-cover rounded"
                            />
                            <button
                              type="button"
                              onClick={() => removeSectionImage(index)}
                              aria-label="Remove section image"
                              className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-gray-600 hover:text-red-500 shadow"
                            >
                              <CloseIcon fontSize="small" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={addSection}
                className="inline-flex items-center gap-1.5 border border-dashed border-[#3A683F] text-[#3A683F] px-4 py-2 rounded font-lexend text-[14px] hover:bg-[#EEF4EA] transition mt-6"
              >
                <AddIcon sx={{ fontSize: 16 }} /> Add section
              </button>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="bg-[#3A683F] text-white font-lexend text-[16px] px-8 py-3 rounded hover:opacity-90 transition disabled:opacity-50"
              >
                {submitting ? "Saving..." : isEditMode ? "Update Blog" : "Publish Blog"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}